<template>
  <div>
    <vx-card no-shadow>
      <h2 class="mb-5">
        {{ $t('CardTitle') }}
      </h2>

      <complete-activation-steps
        :title="$t('CompleteSecurityStepsTitle')"
        :subtitle="$t('CompleteSecurityStepsSubtitle')"
        :chart-title="$t('CompleteSecurityStepsChartTitle')"
        :steps="activationsStepsMapped"
        no-card
        @google="scrollInto('google')"
        @2fa="scrollInto('2fa')"/>
    </vx-card>

    <vx-card
      no-shadow
      class="mt-base">
      <div class="vx-row mb-base pb-base">
        <div class="vx-col w-full lg:w-1/2 mt-base pt-base lg:mt-0 lg:pt-0">
          <user-account-profile-password/>
        </div>
      </div>

      <div class="vx-row mb-base pb-base">
        <div class="vx-col w-full lg:w-1/2">
          <user-account-profile-security ref="2fa"/>
        </div>
        <div class="vx-col w-full lg:w-1/2 justify-start mt-base pt-base lg:mt-0 lg:pt-0">
          <user-account-profile-accounts ref="google"/>
        </div>
      </div>
    </vx-card>
  </div>

</template>

<script>
import { mapGetters } from 'vuex';

// Components
import CompleteActivationSteps from '@/views/_components/CompleteActivationSteps.vue';
import UserAccountProfilePassword from '@/views/modules/user/user-account/user-account-details/UserAccountProfilePassword.vue';
import UserAccountProfileSecurity from '@/views/modules/user/user-account/user-account-details/UserAccountProfileSecurity.vue';
import UserAccountProfileAccounts from '@/views/modules/user/user-account/user-account-details/UserAccountProfileAccounts.vue';

/**
 * User account security recommendations
 *
 * @module views/modules/user-account/UserAccountSecurity.vue
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 * @vue-data {Object[]} [recommendationsSteps=[...]] - recommendations steps to show
 * @vue-computed {Object[]} [activationsStepsMapped=[...]] - recommendations steps to show mapped
 */
export default {
  name: 'UserAccountSecurity',
  i18n: {
    messages: {
      en: {
        CardTitle: 'Keep your account safe',
        CompleteSecurityStepsTitle: 'Security recommendations',
        CompleteSecurityStepsSubtitle: 'Follow these steps to enhance your account security',
        CompleteSecurityStepsChartTitle: 'Security steps',
        $SecuritySteps: {
          $UseGoogleAccount: {
            Title: 'Sign in with Google',
            Subtitle: 'Google’s security features can help detect and alert you to unusual activity.',
          },
          $EnabledTwoFactorAuth: {
            Title: 'Enable two-factor authentication',
            Subtitle: 'Adding 2FA provides an extra layer of protection, ensuring your account stays safe even if your password is compromised.',
          },
        },
      },
    },
  },
  components: {
    CompleteActivationSteps,
    UserAccountProfilePassword,
    UserAccountProfileSecurity,
    UserAccountProfileAccounts,
  },
  data() {
    return {
      recommendationsSteps: [
        {
          title: this.$t('$SecuritySteps.$UseGoogleAccount.Title'),
          subtitle: this.$t('$SecuritySteps.$UseGoogleAccount.Subtitle'),
          event: 'google',
          icon: 'icon-lock',
          completed: false,
        },
        {
          title: this.$t('$SecuritySteps.$EnabledTwoFactorAuth.Title'),
          subtitle: this.$t('$SecuritySteps.$EnabledTwoFactorAuth.Subtitle'),
          event: '2fa',
          icon: 'icon-shield',
          completed: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getUserFlag: 'auth/getUserFlag',
      userHasGoogleEmail: 'auth/hasGoogleEmail',
    }),
    activationsStepsMapped() {
      const recommendationsSteps = [...this.recommendationsSteps];

      recommendationsSteps[0].completed = this.userHasGoogleEmail;
      recommendationsSteps[1].completed = this.getUserFlag('twoFactorAuthentication');

      return recommendationsSteps;
    },
  },

  methods: {
    scrollInto(el) {
      this.$refs[el].$el.scrollIntoView({ behavior: 'smooth' });
    },
  },
};
</script>
